import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import heroBackground from '../assets/images/hero-background.png';
import rightCaretIcon from '../assets/icons/right-caret.svg';
import Footer from '../components/Footer';
import { blogApi, BlogArticle } from '../services/blogApi';

const BlogDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [htmlContent, setHtmlContent] = useState<string>('');

  useEffect(() => {
    const loadArticle = async () => {
      if (!id) {
        setError('Article ID not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const articleData = await blogApi.getArticle(id);
        setArticle(articleData);

        // Convert Markdown to HTML
        if (articleData && articleData.content) {
          const html = await marked(articleData.content);
          setHtmlContent(html);
        }
      } catch (err) {
        console.error('Error loading article:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        gap: 2
      }}>
        <Alert severity="error">{error || 'Article not found'}</Alert>
        <Typography
          onClick={() => navigate('/blog')}
          sx={{
            cursor: 'pointer',
            textDecoration: 'underline',
            color: 'primary.main'
          }}
        >
          ← Back to blog
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '1920px',
      minHeight: '100vh',
      backgroundColor: '#F2F2F2',
      position: 'relative',
      margin: '0 auto',
      px: { xs: 2, sm: 3, md: 4, lg: 6 }
    }}>
      {/* Main Content Container */}
      <Box sx={{ 
        position: 'relative',
        top: { xs: '100px', md: '140px' },
        width: '100%',
        maxWidth: '804px',
        mx: 'auto',
        mb: { xs: '60px', md: '100px' }
      }}>
        {/* Article Header */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '24px', md: '32px' },
          width: '100%',
          mb: { xs: '24px', md: '32px' }
        }}>
          {/* Breadcrumb */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            width: '100%'
          }}>
            <Typography
              onClick={() => navigate('/')}
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '11px',
                lineHeight: '14.3px',
                letterSpacing: '0.91%',
                textAlign: 'center',
                color: '#4C4C4C',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Domů
            </Typography>
            <Box
              component="img"
              src={rightCaretIcon}
              alt=">"
              sx={{
                width: '10px',
                height: '10px'
              }}
            />
            <Typography
              onClick={() => navigate('/blog')}
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '11px',
                lineHeight: '14.3px',
                letterSpacing: '0.91%',
                textAlign: 'center',
                color: '#4C4C4C',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Blog
            </Typography>
            <Box
              component="img"
              src={rightCaretIcon}
              alt=">"
              sx={{
                width: '10px',
                height: '10px'
              }}
            />
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '11px',
              lineHeight: '14.3px',
              letterSpacing: '0.91%',
              textAlign: 'center',
              color: '#797979'
            }}>
              {article.title.length > 50 ? `${article.title.substring(0, 50)}...` : article.title}
            </Typography>
          </Box>

          {/* Title */}
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 600,
            fontSize: { xs: '24px', sm: '28px', md: '36px' },
            lineHeight: { xs: '32px', sm: '36px', md: '44px' },
            letterSpacing: '2.78%',
            color: '#1F1F1F',
            width: '100%',
            textAlign: { xs: 'center', md: 'left' }
          }}>
            {article.title}
          </Typography>
        </Box>

        {/* Hero Image */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: '200px', sm: '260px', md: '340px' },
            borderRadius: { xs: '16px', md: '20px' },
            overflow: 'hidden',
            backgroundImage: `url(${article.imageUrl || heroBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mb: { xs: '24px', md: '32px' }
          }}
        />

        {/* Article Meta Info */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: '24px', md: '32px' }
        }}>
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '18px',
            color: '#797979'
          }}>
            {new Date(article.date).toLocaleDateString('cs-CZ')} • {article.readTime} čtení
          </Typography>
          {article.aiGenerated && (
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: '10px',
              backgroundColor: '#E3F2FD',
              color: '#1976D2',
              px: 1,
              py: 0.5,
              borderRadius: '12px'
            }}>
              AI
            </Typography>
          )}
        </Box>

        {/* Article Description */}
        <Typography sx={{
          fontFamily: 'Inter',
          fontWeight: 400,
          fontSize: { xs: '16px', md: '18px' },
          lineHeight: { xs: '24px', md: '28px' },
          textAlign: 'left',
          color: '#666666',
          fontStyle: 'italic',
          mb: { xs: '24px', md: '32px' }
        }}>
          {article.description}
        </Typography>

        {/* Article Content */}
        <Box sx={{
          fontFamily: 'Inter',
          fontWeight: 400,
          fontSize: { xs: '14px', md: '16px' },
          lineHeight: { xs: '20px', md: '24px' },
          textAlign: 'left',
          color: '#1F1F1F',
          mb: { xs: '40px', md: '60px' },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            fontWeight: 600,
            color: '#1F1F1F',
            mb: { xs: '16px', md: '20px' },
            mt: { xs: '24px', md: '32px' }
          },
          '& h2': {
            fontSize: { xs: '20px', md: '24px' },
            lineHeight: { xs: '28px', md: '32px' }
          },
          '& h3': {
            fontSize: { xs: '18px', md: '20px' },
            lineHeight: { xs: '24px', md: '28px' }
          },
          '& p': {
            mb: { xs: '16px', md: '20px' }
          },
          '& ul, & ol': {
            pl: { xs: '20px', md: '24px' },
            mb: { xs: '16px', md: '20px' }
          },
          '& li': {
            mb: '8px'
          },
          '& a': {
            color: '#1976D2',
            textDecoration: 'underline',
            '&:hover': {
              color: '#1565C0'
            }
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            mb: { xs: '16px', md: '20px' },
            '& th, & td': {
              border: '1px solid #ddd',
              p: { xs: '8px', md: '12px' },
              textAlign: 'left'
            },
            '& th': {
              backgroundColor: '#f5f5f5',
              fontWeight: 600
            }
          },
          '& blockquote': {
            borderLeft: '4px solid #1976D2',
            pl: { xs: '16px', md: '20px' },
            ml: 0,
            fontStyle: 'italic',
            color: '#666666'
          }
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent || 'Content not available.' }}
        />

        {/* Tags */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: { xs: '24px', md: '32px' } }}>
          {article.tags.map((tag, index) => (
            <Typography
              key={index}
              sx={{
                fontFamily: 'Inter',
                fontSize: '12px',
                backgroundColor: '#F5F5F5',
                color: '#666666',
                px: 2,
                py: 1,
                borderRadius: '16px'
              }}
            >
              {tag}
            </Typography>
          ))}
        </Box>
      </Box>
      
      {/* Spacer */}
      <Box sx={{ height: { xs: '60px', md: '80px' } }} />
      
      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default BlogDetailPage;